import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './Predict.css';
import DUAModal, { useDUACheck } from '../components/DUAModal';

const API = '/api/v2';

const STEPS = [
  { id: 'patient', label: 'Patient', icon: '👤' },
  { id: 'lesion', label: 'Lesion', icon: '🔍' },
  { id: 'strategy', label: 'Strategy', icon: '🔧' },
];

function Predict() {
  const { needsDUA, checked: duaChecked, refresh: refreshDUA } = useDUACheck();

  const [campaign, setCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({});
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  useEffect(() => {
    axios.get(API + '/campaigns').then(r => {
      const camps = r.data.campaigns || [];
      setCampaigns(camps);
      const main = camps.find(c => c.patient_count > 0);
      if (main) setCampaign(main.slug);
    });
  }, []);

  const runPrediction = useCallback(async (currentProfile, currentCampaign) => {
    if (!currentCampaign) return;
    const filled = Object.entries(currentProfile).filter(([k, v]) => v !== '' && v !== null && v !== undefined);
    if (filled.length < 2) { setPrediction(null); return; }

    setLoading(true);
    try {
      const r = await axios.post(API + '/campaigns/' + currentCampaign + '/predict', { profile: currentProfile });
      setPrediction(r.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  const updateField = (field, value) => {
    const newProfile = { ...profile, [field]: value };
    setProfile(newProfile);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => runPrediction(newProfile, campaign), 600);
    setDebounceTimer(timer);
  };

  const confidenceColor = (level) => {
    if (level === 'high') return '#059669';
    if (level === 'moderate') return '#d97706';
    if (level === 'low') return '#dc2626';
    return '#94a3b8';
  };

  const riskColor = (rate) => {
    if (rate < 10) return '#059669';
    if (rate < 25) return '#d97706';
    return '#dc2626';
  };

  const filledCount = Object.values(profile).filter(v => v !== '' && v !== null && v !== undefined).length;

  if (needsDUA) return (<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><DUAModal onSigned={refreshDUA} /></div>);

  if (!duaChecked) return null;


  return (
    <div className="predict-layout">
      {/* Left Panel: Input */}
      <div className="predict-input">
        <div className="pi-header">
          <h1>Outcome Simulator</h1>
          <select className="pi-campaign" value={campaign} onChange={e => { setCampaign(e.target.value); setPrediction(null); }}>
            {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name} ({c.patient_count})</option>)}
          </select>
        </div>

        {/* Step Tabs */}
        <div className="step-tabs">
          {STEPS.map((s, i) => (
            <button key={s.id} className={'step-tab' + (step === i ? ' active' : '')} onClick={() => setStep(i)}>
              <span className="st-icon">{s.icon}</span> {s.label}
            </button>
          ))}
        </div>

        {/* Step 1: Patient */}
        {step === 0 && (
          <div className="input-section">
            <h3>Patient Demographics</h3>
            <div className="input-grid">
              <div className="ig">
                <label>Age at surgery</label>
                <div className="slider-row">
                  <input type="range" min="18" max="90" value={profile.age_at_surgery || 50} onChange={e => updateField('age_at_surgery', parseInt(e.target.value))} />
                  <span className="slider-val">{profile.age_at_surgery || 50}</span>
                </div>
              </div>
              <div className="ig">
                <label>Sex</label>
                <div className="toggle-group">
                  {['Male', 'Female'].map(v => (
                    <button key={v} className={'toggle-btn' + (profile.sex === v ? ' active' : '')} onClick={() => updateField('sex', v)}>{v}</button>
                  ))}
                </div>
              </div>
              <div className="ig">
                <label>BMI</label>
                <div className="slider-row">
                  <input type="range" min="16" max="50" step="0.5" value={profile.bmi || 25} onChange={e => updateField('bmi', parseFloat(e.target.value))} />
                  <span className="slider-val">{profile.bmi || 25}</span>
                </div>
              </div>
              <div className="ig">
                <label>Smoking status</label>
                <div className="toggle-group">
                  {['Never', 'Former', 'Current'].map(v => (
                    <button key={v} className={'toggle-btn' + (profile.smoking_status === v ? ' active' : '')} onClick={() => updateField('smoking_status', v)}>{v}</button>
                  ))}
                </div>
              </div>
              <div className="ig">
                <label>ASA Class</label>
                <div className="toggle-group">
                  {['ASA I', 'ASA II', 'ASA III', 'ASA IV'].map(v => (
                    <button key={v} className={'toggle-btn sm' + (profile.asa_class === v ? ' active' : '')} onClick={() => updateField('asa_class', v)}>{v.replace('ASA ', '')}</button>
                  ))}
                </div>
              </div>
            </div>
            <button className="btn-next" onClick={() => setStep(1)}>Next: Lesion →</button>
          </div>
        )}

        {/* Step 2: Diagnosis & Lesion */}
        {step === 1 && (
          <div className="input-section">
            <h3>Diagnosis & Lesion</h3>
            <div className="input-grid">
              {campaign !== 'si-fusion' && <div className="ig full">
                <label>Primary Diagnosis</label>
                <div className="toggle-group wrap">
                  {['Spondylolisthésis dégenératif', 'Discopathie lombaire', 'Sténose lombaire central', 'Hernie discale lombaire', 'Stenose lombaire foraminale'].map(v => (
                    <button key={v} className={'toggle-btn' + (profile.diagnosis_primary === v ? ' active' : '')} onClick={() => updateField('diagnosis_primary', v)}>{v.replace('Spondylolisthésis dégenératif','Spondylolisthesis').replace('Discopathie lombaire','Discopathy').replace('Sténose lombaire central','Central Stenosis').replace('Hernie discale lombaire','Disc Herniation').replace('Stenose lombaire foraminale','Foraminal Stenosis')}</button>
                  ))}
                </div>
              </div>}
              {campaign !== 'si-fusion' && <div className="ig">
                <label>Schizas Grade (stenosis)</label>
                <div className="toggle-group">
                  {['Grade B', 'Grade C', 'Grade D'].map(v => (
                    <button key={v} className={'toggle-btn' + (profile._schizas_grade === v ? ' active' : '')} onClick={() => updateField('_schizas_grade', v)}>{v}</button>
                  ))}
                </div>
              </div>}
              <div className="ig">
                <label>Prior spine surgery</label>
                <div className="toggle-group">
                  {['Yes', 'No'].map(v => (
                    <button key={v} className={'toggle-btn' + (profile.prior_spine_surgery === v ? ' active' : '')} onClick={() => updateField('prior_spine_surgery', v)}>{v}</button>
                  ))}
                </div>
              </div>
              <div className="ig">
                <label>Pre-op ODI (0–100)</label>
                <div className="slider-row">
                  <input type="range" min="0" max="100" value={profile.preop_odi || 40} onChange={e => updateField('preop_odi', parseInt(e.target.value))} />
                  <span className="slider-val">{profile.preop_odi || 40}</span>
                </div>
              </div>
              <div className="ig">
                <label>Pre-op VAS Back (0–10)</label>
                <div className="slider-row">
                  <input type="range" min="0" max="10" value={profile.preop_vas_back || 6} onChange={e => updateField('preop_vas_back', parseInt(e.target.value))} />
                  <span className="slider-val">{profile.preop_vas_back || 6}</span>
                </div>
              </div>
              <div className="ig">
                <label>Pre-op VAS Leg (0–10)</label>
                <div className="slider-row">
                  <input type="range" min="0" max="10" value={profile.preop_vas_leg || 5} onChange={e => updateField('preop_vas_leg', parseInt(e.target.value))} />
                  <span className="slider-val">{profile.preop_vas_leg || 5}</span>
                </div>
              </div>
            </div>
            <div className="step-nav">
              <button className="btn-back" onClick={() => setStep(0)}>← Patient</button>
              <button className="btn-next" onClick={() => setStep(2)}>Next: Strategy →</button>
            </div>
          </div>
        )}

        {/* Step 3: Strategy */}
        {step === 2 && (
          <div className="input-section">
            <h3>Surgical Strategy</h3>
            <div className="input-grid">
              <div className="ig full">
                <label>Approach</label>
                <div className="toggle-group">
                  {(campaign === 'si-fusion' ? ['Percutaneous'] : ['Open', 'MIS', 'Endo']).map(v => (
                    <button key={v} className={'toggle-btn' + (profile.approach_type === v ? ' active' : '')} onClick={() => updateField('approach_type', v)}>{v === 'Open' ? '🔓 Open TLIF' : v === 'MIS' ? '🔬 MIS-TLIF' : v === 'Endo' ? '📷 Endo-TLIF' : '🎯 Percutaneous'}</button>
                  ))}
                </div>
              </div>
              {campaign !== 'si-fusion' && <div className="ig">
                <label>Fusion Levels</label>
                <div className="toggle-group wrap">
                  {['L5S1', 'L4L5', 'L3L4', 'L2L3'].map(v => (
                    <button key={v} className={'toggle-btn sm' + (profile.fusion_levels === v ? ' active' : '')} onClick={() => updateField('fusion_levels', v)}>{v}</button>
                  ))}
                </div>
              </div>}
              {campaign !== 'si-fusion' && <div className="ig">
                <label>Number of levels fused</label>
                <div className="toggle-group">
                  {['1', '2', '3'].map(v => (
                    <button key={v} className={'toggle-btn sm' + (String(profile.num_levels_fused) === v ? ' active' : '')} onClick={() => updateField('num_levels_fused', v)}>{v}</button>
                  ))}
                </div>
              </div>}
            </div>
            <div className="step-nav">
              <button className="btn-back" onClick={() => setStep(1)}>← Diagnosis</button>
            </div>
          </div>
        )}

        <div className="profile-summary">
          <span>{filledCount} field{filledCount !== 1 ? 's' : ''} entered</span>
          <button className="btn-reset" onClick={() => { setProfile({}); setPrediction(null); }}>Reset</button>
        </div>
      </div>

      {/* Right Panel: Predictions */}
      <div className="predict-output">
        {!prediction && !loading && (
          <div className="po-empty">
            <div className="po-empty-icon">📊</div>
            <h2>Outcome Predictions</h2>
            <p>Enter at least 2 patient characteristics to see predicted outcomes from the dataset.</p>
          </div>
        )}

        {loading && (
          <div className="po-loading">
            <div className="spinner"></div>
            <p>Analyzing {campaigns.find(c => c.slug === campaign)?.patient_count || '...'} cases...</p>
          </div>
        )}

        {prediction && !prediction.error && (
          <div className="po-results">
            {/* Disclaimer */}
            <div className="disclaimer">
              ⚠️ Observational data only. Not a medical device. Does not replace clinical judgment. For research and educational purposes only.
            </div>

            {/* Dataset & Confidence */}
            <div className="po-meta">
              {prediction.dataset.highly_similar > 0 && (
                <div className="pm-item highlight">
                  <span className="pm-val">{prediction.dataset.highly_similar}</span>
                  <span className="pm-label">highly similar (&gt;70%)</span>
                </div>
              )}
              <div className="pm-item">
                <span className="pm-val">{prediction.dataset.matched_patients}</span>
                <span className="pm-label">used for analysis</span>
              </div>
              <div className="pm-item">
                <span className="pm-val">{prediction.dataset.top_similarity}%</span>
                <span className="pm-label">best match</span>
              </div>
              <div className="pm-item">
                <span className="pm-val">{prediction.dataset.total_patients}</span>
                <span className="pm-label">total in dataset</span>
              </div>
            </div>
            <div className="sim-range-note">
              Analysis based on {prediction.dataset.matched_patients} closest matches (similarity {prediction.dataset.min_similarity}%–{prediction.dataset.top_similarity}%). Higher similarity = patient profile closer to yours.
            </div>

            <div className="confidence-bar">
              <div className="cb-label">
                Confidence: <strong style={{color: confidenceColor(prediction.confidence.level)}}>{prediction.confidence.label}</strong>
              </div>
              <div className="cb-track">
                <div className="cb-fill" style={{width: prediction.confidence.score + '%', background: confidenceColor(prediction.confidence.level)}}></div>
              </div>
              <span className="cb-score">{prediction.confidence.score}/100</span>
            </div>

            {/* Risk Cards */}
            <div className="risk-cards">
              {prediction.predictions.complication_risk && (
                <div className="risk-card">
                  <div className="rc-rate" style={{color: riskColor(prediction.predictions.complication_risk.rate)}}>
                    {prediction.predictions.complication_risk.rate}%
                  </div>
                  <div className="rc-label">Complication Risk</div>
                  <div className="rc-ci">95% CI: {prediction.predictions.complication_risk.ci_low}–{prediction.predictions.complication_risk.ci_high}%</div>
                  <div className="rc-n">n={prediction.predictions.complication_risk.n} ({prediction.predictions.complication_risk.events} events)</div>
                </div>
              )}
              {prediction.predictions.reoperation_risk && (
                <div className="risk-card">
                  <div className="rc-rate" style={{color: riskColor(prediction.predictions.reoperation_risk.rate)}}>
                    {prediction.predictions.reoperation_risk.rate}%
                  </div>
                  <div className="rc-label">Reoperation Risk</div>
                  <div className="rc-ci">95% CI: {prediction.predictions.reoperation_risk.ci_low}–{prediction.predictions.reoperation_risk.ci_high}%</div>
                  <div className="rc-n">n={prediction.predictions.reoperation_risk.n}</div>
                </div>
              )}
              {prediction.predictions.odi_improvement && (
                <div className="risk-card positive">
                  <div className="rc-rate" style={{color: '#059669'}}>
                    +{prediction.predictions.odi_improvement.mean}
                  </div>
                  <div className="rc-label">ODI Improvement</div>
                  <div className="rc-ci">95% CI: {prediction.predictions.odi_improvement.ci_low}–{prediction.predictions.odi_improvement.ci_high} pts</div>
                  <div className="rc-n">n={prediction.predictions.odi_improvement.n}</div>
                </div>
              )}
            </div>

            {/* Improvement Trajectory */}
            {prediction.predictions.improvement_trajectory && prediction.predictions.improvement_trajectory.length > 0 && (
              <div className="po-chart-card">
                <h3>Patient-Reported Improvement (observed)</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={prediction.predictions.improvement_trajectory} barSize={48}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="timepoint" fontSize={12} />
                    <YAxis domain={[0, 100]} fontSize={11} tickFormatter={v => v + '%'} />
                    <Tooltip formatter={(v) => [v + '%', '']} />
                    <Bar dataKey="mean" fill="#2563eb" radius={[6,6,0,0]} name="Mean Improvement" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="improvement-details">
                  {prediction.predictions.improvement_trajectory.map((d, i) => (
                    <div key={i} className="imp-item">
                      <span className="imp-tp">{d.timepoint}</span>
                      <span className="imp-val">{d.mean}%</span>
                      <span className="imp-ci">95% CI: {d.ci_low}–{d.ci_high}%</span>
                      <span className="imp-n">n={d.n}</span>
                    </div>
                  ))}
                </div>
                <p className="chart-note">Self-reported improvement percentage. 100% = complete resolution.</p>
              </div>
            )}

            {/* Satisfaction */}
            {prediction.predictions.satisfaction && prediction.predictions.satisfaction.length > 0 && (
              <div className="po-chart-card">
                <h3>Patient Satisfaction (observed)</h3>
                <div className="satisfaction-cards">
                  {prediction.predictions.satisfaction.map((s, i) => {
                    const satColors = {
                      'Very Satisfied': { bg: '#d1fae5', color: '#065f46', emoji: '😊' },
                      'Satisfied': { bg: '#e0f2fe', color: '#0c4a6e', emoji: '🙂' },
                      'Neutral': { bg: '#f1f5f9', color: '#475569', emoji: '😐' },
                      'Dissatisfied': { bg: '#fef3c7', color: '#92400e', emoji: '😕' },
                      'Very Dissatisfied': { bg: '#fee2e2', color: '#991b1b', emoji: '😞' },
                    };
                    return (
                      <div key={i} className="sat-card">
                        <div className="sat-tp">{s.timepoint}</div>
                        <div className="sat-pct" style={{color: s.satisfied_pct >= 70 ? '#059669' : s.satisfied_pct >= 50 ? '#d97706' : '#dc2626'}}>{s.satisfied_pct}%</div>
                        <div className="sat-label">satisfied or very satisfied</div>
                        <div className="sat-bar-track">
                          {Object.entries(s.distribution).map(([k, v]) => {
                            const style = satColors[k] || { bg: '#e2e8f0', color: '#475569' };
                            const pct = (v / s.n * 100);
                            return pct > 0 ? <div key={k} className="sat-bar-seg" style={{width: pct + '%', background: style.bg}} title={k + ': ' + v + ' (' + Math.round(pct) + '%)'}></div> : null;
                          })}
                        </div>
                        <div className="sat-legend">
                          {Object.entries(s.distribution).map(([k, v]) => {
                            const style = satColors[k] || { bg: '#e2e8f0', color: '#475569', emoji: '' };
                            return (
                              <div key={k} className="sat-legend-item">
                                <span className="sat-dot" style={{background: style.bg, border: '1px solid ' + style.color}}></span>
                                <span className="sat-cat-label">{(style.emoji || '') + ' ' + k}</span>
                                <span className="sat-cat-val">{v} ({Math.round(v / s.n * 100)}%)</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="sat-n">n={s.n}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Return to Work */}
            {prediction.predictions.return_to_work && prediction.predictions.return_to_work.length > 0 && (
              <div className="po-chart-card">
                <h3>Return to Work (observed)</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={prediction.predictions.return_to_work} barSize={36}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="timepoint" fontSize={12} />
                    <YAxis domain={[0, 100]} fontSize={11} tickFormatter={v => v + '%'} />
                    <Tooltip formatter={(v) => [v + '%', '']} />
                    <Legend />
                    <Bar dataKey="returned_pct" fill="#059669" radius={[6,6,0,0]} name="Returned" />
                    <Bar dataKey="not_returned_pct" fill="#f87171" radius={[6,6,0,0]} name="Not returned" />
                  </BarChart>
                </ResponsiveContainer>
                <p className="chart-note">Work return rates among matched patients at each timepoint.</p>
              </div>
            )}

            {/* Pre-op Baseline */}
            {(prediction.predictions.odi_trajectory?.length > 0 || prediction.predictions.vas_back_trajectory?.length > 0) && (
              <div className="po-chart-card">
                <h3>Pre-operative Baseline (matched patients)</h3>
                <div className="baseline-cards">
                  {prediction.predictions.odi_trajectory?.filter(d => d.timepoint === 'Pre-op').map((d, i) => (
                    <div key={'odi'+i} className="bl-card">
                      <div className="bl-val">{d.mean}</div>
                      <div className="bl-label">ODI Score</div>
                      <div className="bl-ci">{d.ci_low}–{d.ci_high}</div>
                      <div className="bl-n">n={d.n}</div>
                    </div>
                  ))}
                  {prediction.predictions.vas_back_trajectory?.filter(d => d.timepoint === 'Pre-op').map((d, i) => (
                    <div key={'vb'+i} className="bl-card">
                      <div className="bl-val">{d.mean}</div>
                      <div className="bl-label">VAS Back</div>
                      <div className="bl-ci">{d.ci_low}–{d.ci_high}</div>
                      <div className="bl-n">n={d.n}</div>
                    </div>
                  ))}
                  {prediction.predictions.vas_leg_trajectory?.filter(d => d.timepoint === 'Pre-op').map((d, i) => (
                    <div key={'vl'+i} className="bl-card">
                      <div className="bl-val">{d.mean}</div>
                      <div className="bl-label">VAS Leg</div>
                      <div className="bl-ci">{d.ci_low}–{d.ci_high}</div>
                      <div className="bl-n">n={d.n}</div>
                    </div>
                  ))}
                </div>
                <p className="chart-note">Mean pre-operative scores of matched patients. CI = 95% confidence interval.</p>
              </div>
            )}

            {/* Risk Factors */}
            {prediction.risk_factors && prediction.risk_factors.length > 0 && (
              <div className="po-section">
                <h3>Key Factors for This Patient</h3>
                {prediction.risk_factors.map((f, i) => (
                  <div key={i} className={'factor-row ' + f.type}>
                    <span className="fr-icon">{f.type === 'risk' ? '⚠️' : '✅'}</span>
                    <div className="fr-body">
                      <span className="fr-desc">{f.description}</span>
                      <span className="fr-meta">p={f.p_value} · ±{f.impact_odi_points} ODI pts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Strategy Comparison */}
            {prediction.strategy_comparison && Object.keys(prediction.strategy_comparison).length > 0 && (
              <div className="po-section">
                <h3>Strategy Comparison (observed)</h3>
                {Object.entries(prediction.strategy_comparison).map(([field, data]) => (
                  <div key={field} className="strategy-block">
                    <h4>{field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                    <div className="strategy-options">
                      {data.options.map((opt, i) => (
                        <div key={i} className={'so-card' + (opt.is_patient_choice ? ' selected' : '')}>
                          <div className="so-val">{opt.value}</div>
                          <div className="so-improvement">ODI +{opt.avg_odi_improvement}</div>
                          <div className="so-ci">{opt.ci_low}–{opt.ci_high}</div>
                          <div className="so-compl">Complications: {opt.complication_rate}%</div>
                          <div className="so-n">n={opt.n}</div>
                        </div>
                      ))}
                    </div>
                    {data.p_value && <p className="strat-sig">{data.significant ? '✓ Statistically significant' : 'Not statistically significant'} (p={data.p_value})</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Field Importance */}
            {prediction.field_importance && prediction.field_importance.length > 0 && (
              <div className="po-section">
                <h3>Data-Driven Variable Importance</h3>
                <div className="fi-list">
                  {prediction.field_importance.slice(0, 8).map((f, i) => (
                    <div key={i} className="fi-row">
                      <span className="fi-name">{f.field.replace(/_/g, ' ')}</span>
                      <div className="fi-bar-track">
                        <div className="fi-bar" style={{width: (f.weight / 5 * 100) + '%', background: f.significant ? '#2563eb' : '#cbd5e1'}}></div>
                      </div>
                      <span className="fi-sig">{f.significant ? '★' : ''}</span>
                      <span className="fi-p">{f.p_value ? 'p=' + f.p_value : ''}</span>
                    </div>
                  ))}
                </div>
                <p className="chart-note">★ = statistically significant (p &lt; 0.05). Weight derived from outcome correlations.</p>
              </div>
            )}
          </div>
        )}

        {prediction && prediction.error && (
          <div className="po-error">
            <p>⚠️ {prediction.error}</p>
            {prediction.total_patients !== undefined && <p>Dataset has {prediction.total_patients} patients. Minimum {prediction.minimum_required} needed.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function mergeVasData(backData, legData) {
  const map = {};
  (backData || []).forEach(d => { map[d.timepoint] = { timepoint: d.timepoint, back: d.mean }; });
  (legData || []).forEach(d => {
    if (map[d.timepoint]) map[d.timepoint].leg = d.mean;
    else map[d.timepoint] = { timepoint: d.timepoint, leg: d.mean };
  });
  const order = ['Pre-op', '3 mo', '6 mo', '12 mo', '24 mo'];
  return order.map(t => map[t]).filter(Boolean);
}

export default Predict;
